import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { CheckIcon, StarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface PlanSelectionStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

const PlanSelectionStep: React.FC<PlanSelectionStepProps> = ({ onNext, onPrevious }) => {
  const { setValue, watch, trigger } = useFormContext();
  
  // Watch existing form values for smart recommendations
  const industry = watch('industry');
  const companySize = watch('companySize');
  const primaryUseCase = watch('primaryUseCase');
  const formPlan = watch('plan');
  const formBillingCycle = watch('billingCycle');
  
  // Initialize local state from form context
  const [selectedPlan, setSelectedPlan] = useState<string>(formPlan || 'starter');
  const [recommendedPlan, setRecommendedPlan] = useState<string>('starter');
  const [trialSelected, setTrialSelected] = useState<boolean>(true);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>(formBillingCycle || 'monthly');

  // Sync local state with form context when form values change
  useEffect(() => {
    if (formPlan && formPlan !== selectedPlan) {
      setSelectedPlan(formPlan);
      // Auto-disable trial for non-Starter plans
      if (formPlan !== 'starter') {
        setTrialSelected(false);
        setValue('trialSelected', false);
      }
    }
  }, [formPlan, selectedPlan, setValue]);

  const plans = [
    {
      id: 'starter',
      name: 'Starter + Free Trial',
      monthlyPrice: 49,
      yearlyPrice: 39,
      description: 'Essential Fashion & Beauty Intelligence',
      features: [
        '200 API calls/month',
        '3 data sources',
        'Fashion + Beauty trends only',
        'Basic engagement metrics',
        '3 smart alerts',
        'Bi-weekly reports',
      ],
      popular: false,
      bestFor: 'Individuals and small teams',
      hasTrial: true,
    },
    {
      id: 'growth',
      name: 'Growth',
      monthlyPrice: 89,
      yearlyPrice: 69,
      description: 'Enhanced Analytics for Growing Businesses',
      features: [
        '500 API calls/month',
        '4+ enhanced data sources',
        'Momentum classification',
        '7 alerts + basic webhooks',
        'Weekly reports',
        '7-day forecasting',
      ],
      popular: false,
      bestFor: 'Small agencies and growing creators',
      hasTrial: false,
    },
    {
      id: 'professional',
      name: 'Professional',
      monthlyPrice: 129,
      yearlyPrice: 99,
      description: 'Advanced Predictions & Forecasting',
      features: [
        '1,000 API calls/month',
        '5+ data sources',
        'Full demographics',
        '15 alerts + advanced webhooks',
        'Daily briefings',
        '14-day forecasting',
      ],
      popular: false,
      bestFor: 'Agencies and established brands',
      hasTrial: false,
    },
    {
      id: 'business',
      name: 'Business',
      monthlyPrice: 299,
      yearlyPrice: 224,
      description: 'Complete Intelligence Platform',
      features: [
        '5,000 API calls/month',
        'All data sources',
        'Full ML prediction suite',
        'White-label reports',
        '30-day forecasting',
      ],
      popular: false,
      bestFor: 'Large brands and enterprises',
      hasTrial: false,
    },
  ];

  // Smart plan recommendation based on existing form data
  useEffect(() => {
      let recommendation = 'starter';
      
    // Company size logic
    if (companySize === '200+' || companySize === '51-200') {
        recommendation = 'business';
    } else if (companySize === '11-50') {
        recommendation = 'professional';
    } else if (companySize === '2-10') {
        recommendation = 'growth';
      } else {
        recommendation = 'starter';
      }
    
    // Industry adjustments
    if (industry === 'consulting' || industry === 'marketing') {
      if (recommendation === 'starter') recommendation = 'growth';
      else if (recommendation === 'growth') recommendation = 'professional';
    }
    
    // Use case adjustments
    if (primaryUseCase && primaryUseCase.includes('trend-forecasting')) {
      if (recommendation === 'starter') recommendation = 'growth';
      else if (recommendation === 'growth') recommendation = 'professional';
    }
      
      setRecommendedPlan(recommendation);
      // Only set selected plan if no plan is currently selected
      if (!selectedPlan) {
        setSelectedPlan(recommendation);
        setValue('plan', recommendation);
      }
  }, [industry, companySize, primaryUseCase, setValue, selectedPlan]);

  const handlePlanSelect = (planId: string) => {
    setSelectedPlan(planId);
    setValue('plan', planId);
    
    // Auto-disable trial for non-Starter plans
    if (planId !== 'starter') {
      setTrialSelected(false);
      setValue('trialSelected', false);
    }
  };

  const handleNext = async () => {
    const isValid = await trigger(['plan']);
    if (isValid) {
      onNext();
    }
  };

  const canProceed = selectedPlan;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-5"
    >
      {/* Billing Cycle Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
        <div className="text-center">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Billing Cycle</h2>
          <div className="flex items-center justify-center space-x-4 mb-2">
            <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => {
                const newCycle = billingCycle === 'monthly' ? 'yearly' : 'monthly';
                setBillingCycle(newCycle);
                setValue('billingCycle', newCycle);
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                billingCycle === 'yearly' ? 'bg-purple-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
              Yearly
            </span>
                  </div>
          {billingCycle === 'yearly' && (
            <span className="text-sm text-green-600">Save up to 25% with yearly billing</span>
          )}
        </div>
      </div>



      {/* Plan Selection */}
      <div>
        <div className="text-center mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Select Your Plan</h2>
          <p className="text-sm text-gray-600">Choose the plan that best fits your needs</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 max-w-7xl mx-auto">
          {plans.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            const isRecommended = plan.id === recommendedPlan;
            
            return (
              <motion.div
                key={plan.id}
                className={`relative bg-white rounded-xl shadow-lg border-2 p-3 cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col ${
                  isSelected
                    ? 'border-purple-500 ring-2 ring-purple-200 bg-gradient-to-br from-purple-50 to-white'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
                onClick={() => handlePlanSelect(plan.id)}
                whileHover={{ y: -2 }}
              >
                {/* Badges */}
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 z-10 flex gap-2">
                  {plan.hasTrial && (
                    <span className="inline-flex items-center px-3 py-1 bg-purple-50 border border-purple-200 rounded-full text-xs font-medium text-purple-700 shadow-sm whitespace-nowrap">
                      Free Trial
                    </span>
                  )}
                  {isRecommended && !isSelected && (
                    <span className="inline-flex items-center px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs font-medium text-blue-700 shadow-sm whitespace-nowrap">
                      Recommended
                    </span>
                  )}
                </div>
                

                
                {/* Plan Content */}
                <div className={`text-center flex-grow ${plan.hasTrial ? 'mt-5' : 'mt-2'}`}>
                  <h3 className="text-base font-bold text-gray-900 mb-1">{plan.name}</h3>
                  <p className="text-xs text-gray-600 mb-2">{plan.description}</p>
                  
                  {/* Pricing */}
                  <div className="mb-2">
                    {plan.hasTrial ? (
                      <div>
                        <span className="text-lg font-bold text-green-600">Free Trial</span>
                        <div className="text-xs text-gray-500">
                          Then £{billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}/month
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="text-xl font-bold text-purple-600">
                          £{billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}
                        </span>
                        <span className="text-xs text-gray-600">/month</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-xs text-gray-500 mb-3">{plan.bestFor}</p>
                  
                  {/* Features */}
                  <ul className="space-y-0.5 text-xs">
                    {plan.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckIcon className="h-3 w-3 text-green-500 mr-1.5 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 leading-tight">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Selection Status */}
                <div className={`text-center py-2 px-3 rounded-lg font-medium text-xs h-[36px] flex items-center justify-center mt-4 ${
                  isSelected
                    ? 'bg-purple-100 text-purple-700 border border-purple-200'
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                }`}>
                  {isSelected ? 'Selected' : 'Click to select'}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>



      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onPrevious}
          className="px-6 py-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
        >
          ← Previous
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed}
          className="px-8 py-3 text-lg font-semibold text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {selectedPlan === 'starter' ? 'Start Your Trial →' : 'Continue →'}
        </button>
      </div>
    </motion.div>
  );
};

export default PlanSelectionStep;

